import * as React from 'react';
import { Check } from 'lucide-react';
import { t, tryT } from '@reos/i18n';
import type { MessageKey } from '@reos/i18n';
import {
  createSignatureSession,
  evidencePackage,
  listSignatureSessions,
  printPath,
  remindSignature,
  sendSignatureLink,
  signaturePolicy,
  signatureTimeline,
  stopSignatureSession,
  type ContractSignatureState,
  type EvidencePackageDto,
  type IssuedSignatureLinkDto,
  type PrintPathDto,
  type SignaturePolicyDto,
  type SignatureSessionDto,
  type SignerStatus,
} from '../lib/signature-api.js';
import { DomainApiError } from '../lib/api-error.js';
import { retryTornMutation } from '../lib/rate-limit-retry.js';
import { CLIENT_MAIL_COPY_HINT_KEY, clientMailReasonKey } from '../lib/client-mail-message.js';
import { signerFallbackName, type SigningCondition } from '../lib/contract-presentation.js';
import type {
  ContractFillPlanDto,
  SigningBlockerDto,
  SigningReadinessDto,
} from '../lib/contracts-api.js';
import { V4Group } from './ux-v4.js';

/**
 * Podpis smlouvy v REOS — panel v záložce „Podpis" (krok ES1 body 1, 4, 5, 6).
 *
 * NAHRADIL mockupový panel, který uměl jen přepnout stav smlouvy. Tenhle
 * panel vidí to, co se doopravdy děje: kdo podepsal, v jakém pořadí, jakou
 * úrovní, jestli je dokument zapečetěný, a CO JEŠTĚ CHYBÍ k důkaznímu
 * balíčku.
 *
 * TŘI VĚCI, KTERÉ JSOU TU ZAMYSLENÉ A STOJÍ ZA NIMI:
 *
 * 1. **Úroveň podpisu se nevolí, zobrazuje se.** Dokud se zobrazovalo
 *    „prostý / kvalifikovaný / tisk“ jako volitelné přepínače, makler mohl
 *    „kvalifikovaný“ jen vybrat a nikdo nevěděl, kdo podpis vlastně provedl.
 *    Teď panel píše, co politika pro tuto smlouvu ROZHODLA, a proč — a
 *    když kancelář žádá méně, než je minimum, píše to (`floorApplied`).
 *
 * 2. **„Čeká na ostatní“ není „podepsáno“.** Seznam zobrazuje stav každé
 *    strany ZVLÁŠŤ a stav smlouvy odvozený ze všech (`state`), protože
 *    smlouva, kde podepsal nájemce a pronajímatel ještě neviděl odkaz, NENÍ
 *    podepsaná.
 *
 * 3. **Důkazní balíček ukazuje, co chybí.** Ne „hotovo“, ale seznam
 *    (`completeness.missing`) — a pokud se text smlouvy od podpisu změnil,
 *    pečeť to ohlásí. To je jediná věta, kterou makl potřebuje, aby věděl,
 *    že balíček nesmí poslat.
 */

const LOCALE = 'cs' as const;

/** Chyba API tak, jak ji umi vysvetlit (stejny helper jako `contracts.tsx`). */
function errorMessage(error: unknown): string | undefined {
  return error instanceof DomainApiError ? error.message : undefined;
}

export interface SignaturePanelProps {
  readonly contractId: string;
  /**
   * Jména a kontakty stran ze slovníku smluv (aby nešlo o psaní jmen dvakrát).
   *
   * ES2 bod 2: `name` je `null`, dokud maklér jméno nezná — a NESMÍ se
   * nahrazovat jakýmkoli textem obrazovky. Dřív tu byl `contact: string`, který
   * měl jako fallback text tlačítka „Poslat odkaz“, a panel ho posílal do API
   * jako jméno strany.
   */
  readonly parties: readonly {
    readonly role: string;
    readonly index: number;
    readonly name: string | null;
    readonly email: string | null;
    readonly phone: string | null;
  }[];
  /** Vybrany zpusob odeslani (e-mail / SMS / jen zkopirovat). */
  readonly channel: 'email' | 'sms' | 'odkaz';
  /**
   * Krok SML1 bod 3: HLAVNI PODMINKY na karte strany, ktera je na rade.
   * Sklada je `signingConditions` Z POLI SMLOUVY — panel si je nepise sam,
   * jinak by strana pred podpisem cetla neco jineho, nez co podepisuje.
   */
  readonly conditions?: readonly SigningCondition[] | undefined;
  /**
   * Krok PIL3 bod 2 (nalez F03): PRIPRAVENOST K PODPISU ZE SERVERU.
   *
   * Do 5. 10. 2026 slo zalozit podepisovani i nad smlouvou s 22 chybejicimi
   * udaji a klient dostal odkaz na text s `[…]` a `má/nemá`. Panel si
   * pripravenost NEPOCITA — cte ji z `ContractDetailDto.signing`, tedy
   * z teze funkce, ktera bezi i na brane (`createSignatureSession`).
   */
  readonly signing?: SigningReadinessDto | undefined;
  /**
   * Krok APP3 bod 1 (nalez A31) — BRANA A POCITADLA JMENUJI TOTEZ.
   *
   * Demo smlouva Sojovice ukazovala „K podpisu zbývá 9" u tlacitka v editoru
   * a „10 chybí" v panelu kontrol vedle nej, protoze kazde cislo vznikalo
   * z jine mnoziny. Osnova ze serveru (`fillPlan`) je ted jedina mnozina
   * a panel z ni rika i VZTAH: kolik udaju zbyva celkem a kolik z nich stoji
   * podpisu v ceste (ostatni doplni strany samy na podpisove strance,
   * ROZHODNUTI PIL3-2).
   */
  readonly fillPlan?:
    | Pick<ContractFillPlanDto, 'missing' | 'later' | 'blocking'>
    | undefined;
  /**
   * Krok TOKYFIX1 (prekazka P6/2): panel sam na „Vyplnit" neprepina —
   * zalozky drzi editor smlouvy, takze prepnuti dostane odtud.
   */
  readonly onOpenChoices?: (() => void) | undefined;
}

type BusyKind = 'none' | 'create' | 'send' | 'remind' | 'stop' | 'load';

/**
 * Krok PIL3 bod 2 (nalez F03) — CO PRESNE BRANI PODPISU. Kody jsou serverove
 * (`signing-readiness.ts`), veta patri obrazovce; kazdy kod rika i to, co
 * s tim makler(ka) ma udelat.
 */
const SIGNING_BLOCKER_KEY: Readonly<Record<SigningBlockerDto['code'], MessageKey>> = {
  'chybi-povinne': 'webAdmin.signature.blocker.chybiPovinne',
  'kontrola-chyba': 'webAdmin.signature.blocker.kontrolaChyba',
  'zastupny-text': 'webAdmin.signature.blocker.zastupnyText',
  'nevyresena-alternativa': 'webAdmin.signature.blocker.nevyresenaAlternativa',
};

const SIGNER_STATUS_KEY: Readonly<Record<SignerStatus, MessageKey>> = {
  ceka: 'webAdmin.signature.signer.ceka',
  'k-odeslani': 'webAdmin.signature.signer.k_odeslani',
  odeslano: 'webAdmin.signature.signer.odeslano',
  otevreno: 'webAdmin.signature.signer.otevreno',
  souhlaseno: 'webAdmin.signature.signer.souhlaseno',
  podepsano: 'webAdmin.signature.signer.podepsano',
  odmito: 'webAdmin.signature.signer.odmito',
  zneplatneno: 'webAdmin.signature.signer.zneplatneno',
  zastaveno: 'webAdmin.signature.signer.zastaveno',
};

const STATE_KEY: Readonly<Record<ContractSignatureState, MessageKey>> = {
  nepodepsano: 'webAdmin.signature.state.nepodepsano',
  'ceka-na-strany': 'webAdmin.signature.state.ceka_na_strany',
  'cekaji-ostatni': 'webAdmin.signature.state.cekaji_ostatni',
  podepsano: 'webAdmin.signature.state.podepsano',
  zastaveno: 'webAdmin.signature.state.zastaveno',
  neplatne: 'webAdmin.signature.state.neplatne',
};

/**
 * CO BRANI PODPISU — jedna veta pro VSECHNY cesty k zalozeni podepisovani
 * (prvni kolo i nove kolo po zastaveni, krok PRF4 vada V-NEW3). Hlaska je
 * jedna a na jednom miste; dve kopie by se rozesly se serverovou branou
 * (`signingReadiness`).
 */
function NotReadyNote({
  signing,
  fillPlan,
  onOpenChoices,
}: {
  readonly signing: SigningReadinessDto | undefined;
  readonly fillPlan?: Pick<ContractFillPlanDto, 'missing' | 'later' | 'blocking'> | undefined;
  /**
   * Krok TOKYFIX1 (prekazka P6/2): „Rozhodnout volbu" — prepne na „Vyplnit"
   * a posune na oddil „Volby v textu". Bez odkazu makler(ka) v mereni TOKY1
   * (14. 10. 2026) z hlasky nepoznal(a), KDE volbu rozhodnout.
   */
  readonly onOpenChoices?: (() => void) | undefined;
}): React.JSX.Element | null {
  if (signing === undefined || signing.ready) return null;
  const choices = signing.choices ?? [];
  return (
    <p className="rh-v4-sig__warn" data-testid="signature-panel-not-ready">
      {t('webAdmin.signature.notReady', { locale: LOCALE })}
      {/*
        Krok APP3 bod 1 (nalez A31): VZTAH mezi cislem u tlacitka („zbývá N")
        a cislem brany. Obe jdou z teze osnovy, takze veta nemuze tvrdit
        rozpor; kdyz se cisla lisi, lisi se proto, ze cast udaju doplni
        strany samy (ROZHODNUTI PIL3-2), a veta to rekne.
      */}
      {fillPlan !== undefined && fillPlan.missing > 0 && (
        <>
          {' '}
          <span data-testid="signature-panel-counts">
            {t(
              fillPlan.blocking === fillPlan.missing
                ? 'webAdmin.signature.counts'
                : 'webAdmin.signature.countsParties',
              {
                locale: LOCALE,
                values: { missing: fillPlan.missing, blocking: fillPlan.blocking },
              },
            )}
          </span>
        </>
      )}
      {signing.blockers.map((blocker) => (
        <span key={blocker.code} data-testid={`signature-panel-blocker-${blocker.code}`}>
          {' '}
          {/*
            Krok PRF1 (vady V1a, V5): kdyz server vi, KTERA pole prekazku
            zpusobila, hlaska je vyjmenuje. Veta „1 zástupné místo v textu
            (např. „[…]" nebo „xxxx")" v pilotu 13. 10. stala u smlouvy,
            v jejimz textu nic takoveho nebylo — slo o nevyplneny „Termín
            předání". Tentyz klic uz server nehlasi dvema prekazkami
            (PRF4 V-NEW4), takze veta „Chybí: …" nestoji dvakrat za sebou.
          */}
          {blocker.fields.length > 0
            ? t('webAdmin.signature.blocker.pole', {
                locale: LOCALE,
                values: { fields: blocker.fields.join(', ') },
              })
            : t(SIGNING_BLOCKER_KEY[blocker.code], {
                locale: LOCALE,
                values: { count: blocker.count },
              })}
          {/*
            Krok SMLQA1b: kdyz prekazku nepopisuje zadne POLE, hlaska ukaze
            CLANEK A VYREZ TEXTU, ktery zustal (`blocker.sample` ze serveru).
            Zivy pruchod 30. 10. 2026 skoncil u vety „1 zástupné místo
            v textu (např. „[...]" nebo „xxxxx")" a makléřka nemela z ceho
            poznat, co a kde doplnit — server to pritom uz posilal.
          */}
          {blocker.fields.length === 0 &&
            blocker.sample !== null &&
            blocker.sample !== '' && (
              <>
                {' '}
                <span data-testid={`signature-panel-blocker-sample-${blocker.code}`}>
                  {t('webAdmin.signature.blocker.vTextu', {
                    locale: LOCALE,
                    values: { sample: blocker.sample },
                  })}
                </span>
              </>
            )}
          {/* Krok TOKYFIX1 (prekazka P6/2): odkaz PRIMO na volbu ve formulari. */}
          {blocker.code === 'nevyresena-alternativa' && choices.length > 0 && onOpenChoices !== undefined && (
            <>
              {' '}
              <button
                type="button"
                className="rh-btn rh-btn--plain"
                onClick={onOpenChoices}
                data-testid="signature-panel-open-choices"
              >
                {t('webAdmin.signature.blocker.openChoices', {
                  locale: LOCALE,
                  values: { count: choices.length },
                })}
              </button>
            </>
          )}
        </span>
      ))}
    </p>
  );
}

function keyOfLevel(level: string): MessageKey {
  if (level === 'zaruceny') return 'webAdmin.signature.level.zaruceny';
  if (level === 'kvalifikovany') return 'webAdmin.signature.level.kvalifikovany';
  return 'webAdmin.signature.level.prosty';
}

function keyOfRoute(route: string): MessageKey {
  const map: Readonly<Record<string, MessageKey>> = {
    'magic-link': 'webAdmin.signature.route.magic_link',
    signi: 'webAdmin.signature.route.signi',
    'bankid-sign': 'webAdmin.signature.route.bankid_sign',
    'bankid-qsign': 'webAdmin.signature.route.bankid_qsign',
    'papir-overeny': 'webAdmin.signature.route.papir_overeny',
  };
  return map[route] ?? 'webAdmin.signature.route.magic_link';
}

/**
 * Druh udalosti casove osy -> lidska veta.
 *
 * Do teto opravy se v casove ose ukazoval STROJOVY ENUM (`predano-poskytovateli`,
 * `podpis-zneplatnen`), tedy text, ktery neni v katalogu a neexistuje v en
 * (CLAUDE.md §1 bod 5). Klice katalogu uz existovaly — jen je nikdo nepouzival.
 *
 * Neznamy druh z NOVEJSI verze API obrazovku nesmi shodit (`t` na neznamem
 * klici vyhazuje — nalez A5 N4), proto `tryT` a fallback na surovy druh: radeji
 * strojove slovo u jedne radky nez prazdna obrazovka podpisu.
 */
function timelineEventLabel(kind: string): string {
  const key = `documents.signature.event.${kind.replace(/-/g, '_')}` as MessageKey;
  return tryT(key, { locale: LOCALE }) ?? kind;
}

/**
 * Kanal, kterym se odkaz k podpisu DA poslat. Vybrany kanal je preference
 * makleře, ne slib: strana bez e-mailu se e-mailem neobesle a strana bez
 * telefonu SMS. Zbyva „zkopirovat odkaz“ — a to je stav, ktery panel umi
 * ukazat (ES2 bod 3).
 */
function channelFor(
  party: { readonly email: string | null; readonly phone: string | null },
  preferred: 'email' | 'sms' | 'odkaz',
): 'email' | 'sms' | 'odkaz' {
  if (preferred === 'email' && party.email !== null) return 'email';
  if (preferred === 'sms' && party.phone !== null) return 'sms';
  return 'odkaz';
}

/** „1. 10. 14:12" — datum a hodina, nic vic (predloha i historie). */
function formatMoment(iso: string): string {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return iso;
  return at.toLocaleString('cs-CZ', {
    day: 'numeric',
    month: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** Posledni tri cislice telefonu — predloha: „na číslo končící 412". */
const PHONE_SUFFIX_LENGTH = 3;

function phoneSuffix(phone: string | null): string | null {
  if (phone === null) return null;
  const digits = phone.replace(/\D/g, '');
  return digits.length < PHONE_SUFFIX_LENGTH ? null : digits.slice(-PHONE_SUFFIX_LENGTH);
}

/**
 * Cim byl podpis overen. Kdyz strana SMS kod nepotrebovala, panel nesmi
 * tvrdit, ze ho overila; kdyz cislo neznáme, rekne se jen „ověřeno SMS kódem".
 */
function smsProofText(
  signer: { readonly requiresSms: boolean; readonly smsVerified: boolean },
  phone: string | null,
): string {
  if (!signer.requiresSms) return t('webAdmin.signature.smsNotRequired', { locale: LOCALE });
  if (!signer.smsVerified) return t('webAdmin.signature.smsRequired', { locale: LOCALE });
  const suffix = phoneSuffix(phone);
  return suffix === null
    ? t('webAdmin.signature.smsVerifiedPlain', { locale: LOCALE })
    : t('webAdmin.signature.smsVerifiedSuffix', { locale: LOCALE, values: { suffix } });
}

/**
 * Hodnota podminky na karte. „Změny proti mustru" nese POCET prepsanych bloku
 * (`manualOverrides`), ostatni hotovou hodnotu z poli; nevyplnena podminka se
 * nevynecha, rekne se „nevyplněno".
 */
function conditionValue(item: SigningCondition): string {
  if (item.changeCount !== undefined) {
    return item.changeCount === 0
      ? t('webAdmin.contracts.signature.conditionChangesNone', { locale: LOCALE })
      : t('webAdmin.contracts.signature.conditionChangesSome', {
          locale: LOCALE,
          values: { count: item.changeCount },
        });
  }
  return item.value ?? t('webAdmin.contracts.signature.conditionNotFilled', { locale: LOCALE });
}

/**
 * Dukazni balicek ke stazeni. REOS nema export endpoint — balicek je ODPOVED
 * `/evidence` (pecet, auditni stopa, overeni podpisu, co chybi), takze se
 * uklada presne to, co server vydal, a nic se k nemu nedopisuje.
 */
function downloadEvidence(evidence: EvidencePackageDto): void {
  if (typeof window === 'undefined' || typeof URL.createObjectURL !== 'function') return;
  const blob = new Blob([JSON.stringify(evidence, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `dukazni-balicek-${evidence.contractId}.json`;
  link.click();
  URL.revokeObjectURL(url);
}

export function SignaturePanel({
  contractId,
  parties,
  channel,
  conditions = [],
  signing,
  fillPlan,
  onOpenChoices,
}: SignaturePanelProps): React.JSX.Element {
  const [policy, setPolicy] = React.useState<SignaturePolicyDto | null>(null);
  const [session, setSession] = React.useState<SignatureSessionDto | null>(null);
  const [timeline, setTimeline] = React.useState<
    ReadonlyArray<{ kind: string; createdAt: string; partyId: string | null }>
  >([]);
  const [evidence, setEvidence] = React.useState<EvidencePackageDto | null>(null);
  const [print, setPrint] = React.useState<PrintPathDto | null>(null);
  const [link, setLink] = React.useState<{ url: string; absolute: boolean } | null>(null);
  /**
   * Krok MAIL1 bod 3 — proč e-mail s odkazem neodešel. Odkaz je vydaný a je
   * v boxu vedle, takže akce neskončila chybou: panel jen musí říct pravdu
   * („odesílání je vypnuté, odkaz zkopírujte"), ne tvrdit odeslání.
   */
  const [notSent, setNotSent] = React.useState<string | null>(null);
  const [copied, setCopied] = React.useState(false);
  /**
   * Souhlas pred podpisem (predloha: „Přečetl jsem si smlouvu a souhlasím…").
   * V adminu ho zaskrtava makler ZA SEBE — podpis strany vznika az na jeji
   * podpisove strance, kde ma souhlas vlastni.
   */
  const [consent, setConsent] = React.useState(false);
  const [busy, setBusy] = React.useState<BusyKind>('none');
  const [error, setError] = React.useState<string | null>(null);
  /**
   * Krok TOKYFIX4 bod 2 — DOKUD SE SEZENI NACITA, PANEL NIC NETVRDI.
   * `session` zacina na `null` a to znamena „nepodepisuje se" — takze panel
   * nad ROZEPSANYM podepisovanim nejdriv ukazal „Založit podepisování"
   * a teprve po odpovedi serveru poradi stran. Na telefonu (mereni P6/mobil
   * 19. 10. 12:52) to trvalo dost dlouho na to, aby makleřka videla (a mohla
   * stisknout) zalozeni DRUHEHO sezeni misto sveho podpisu.
   */
  const [loaded, setLoaded] = React.useState(false);

  /**
   * Zkopiruje odkaz do schranky. Bez `navigator.clipboard` (starsi prohlizec,
   * stranka bez https) se nic nepredstira — odkaz je vypsany vedle a makler ho
   * oznaci rucne.
   */
  async function copyLink(url: string): Promise<void> {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  }

  /**
   * Krok MAIL1 bod 3 — věta „e-mail se neodeslal + odkaz zkopírujte".
   *
   * Kanál „jen zkopírovat" (`odkaz`) sem nepatří: tam se zámerně nic neposílá
   * a hláška o neodeslání by byla falešný poplach.
   */
  function notSentMessage(issued: IssuedSignatureLinkDto | null): string | null {
    if (issued === null || issued.delivered || issued.channel === 'odkaz') return null;
    const reason = issued.notDeliveredReason ?? null;
    if (reason === null) return null;
    return `${t(clientMailReasonKey(reason), { locale: LOCALE })} ${t(CLIENT_MAIL_COPY_HINT_KEY, { locale: LOCALE })}`;
  }

  const reload = React.useCallback(async () => {
    setBusy('load');
    setError(null);
    try {
      const loaded = await signaturePolicy(contractId);
      setPolicy(loaded);
      const sessions = await getSession(contractId);
      setSession(sessions);
      if (sessions === null) {
        setTimeline([]);
        setEvidence(null);
        setPrint(null);
        return;
      }
      setTimeline(
        (await signatureTimeline(sessions.id)).map((e) => ({
          kind: e.kind,
          createdAt: e.createdAt,
          partyId: e.partyId,
        })),
      );
      setEvidence(await evidencePackage(sessions.id));
      setPrint(await printPath(sessions.id));
    } catch (cause: unknown) {
      setError(errorMessage(cause) ?? t('webAdmin.signature.actionFailed', { locale: LOCALE }));
    } finally {
      // I kdyz nacteni skoncilo chybou, panel uz dal necha mluvit `error` —
      // mlcet do nekonecna by bylo horsi nez rict, co se nepovedlo.
      setLoaded(true);
      setBusy('none');
    }
  }, [contractId]);

  React.useEffect(() => {
    void reload();
  }, [reload]);

  async function create(): Promise<void> {
    setBusy('create');
    setError(null);
    try {
      /*
       * Krok TOKYFIX5 bod 2 — PRETRZENE SPOJENI NESMI ZASTAVIT PODEPISOVANI.
       * Kdyz `fetch` padne jeste nez prisla odpoved serveru, zalozeni se
       * zkusi znovu — ale teprve po tom, co se CTENIM overi, ze prvni pokus
       * sezeni nezalozil. Duvod a hranice jsou u `retryTornMutation`.
       */
      await retryTornMutation(
        () => createSignatureSession(telo()),
        async () => (await getSession(contractId)) !== null,
      );
      setLink(null);
      setCopied(false);
      await reload();
    } catch (cause: unknown) {
      setError(errorMessage(cause) ?? t('webAdmin.signature.actionFailed', { locale: LOCALE }));
    } finally {
      setBusy('none');
    }

    /** Telo pozadavku — jedno misto pro oba pokusy (viz `retryTornMutation`). */
    function telo(): Parameters<typeof createSignatureSession>[0] {
      return {
        contractId,
        parties: parties.map((p, index) => ({
          // ES2 bod 2: jmeno, ktere makler nezna, se POSILA PRAZDNE. Dřív se
          // misto nej poslal `contact` — jehoz fallbackem byl text tlacitka
          // („Poslat odkaz“), takze se z popisku obrazovky stalo jmeno strany.
          name: p.name ?? '',
          partyKey: p.role,
          // Index V RÁMCI ROLE (`najemce[0]`), ne pozice v plochém seznamu —
          // jinak se z prvního nájemce stane „Nájemce 2“ (živý nález ES2).
          partyIndex: p.index,
          order: index,
          // Kanal podle toho, co u strany OPRAVDU je: bez e-mailu nema
          // „poslat e-mailem“ smysl a server by to odmitl jako chybejiciho
          // prijemce — makler misto odkazu dostal chybu (ES2 bod 3).
          channel: channelFor(p, channel),
          ...(p.email !== null ? { email: p.email } : {}),
          ...(p.phone !== null ? { phone: p.phone } : {}),
        })),
        send: true,
      };
    }
  }

  async function send(): Promise<void> {
    if (session === null) return;
    setBusy('send');
    setError(null);
    try {
      const issued = await sendSignatureLink(session.id, channel);
      // Odkaz se vrací JEDINKRÁT — v DB je jen jeho hash. Kdyby ho panel
      // zahodil, makler by musel založit podepisování znovu.
      setCopied(false);
      setLink(issued === null ? null : { url: issued.link, absolute: issued.absolute });
      setNotSent(notSentMessage(issued));
      await reload();
    } catch (cause: unknown) {
      setError(errorMessage(cause) ?? t('webAdmin.signature.actionFailed', { locale: LOCALE }));
    } finally {
      setBusy('none');
    }
  }

  async function remind(): Promise<void> {
    if (session === null) return;
    setBusy('remind');
    setError(null);
    try {
      await remindSignature(session.id);
      await reload();
    } catch (cause: unknown) {
      setError(errorMessage(cause) ?? t('webAdmin.signature.actionFailed', { locale: LOCALE }));
    } finally {
      setBusy('none');
    }
  }

  async function stop(): Promise<void> {
    if (session === null) return;
    if (
      typeof window !== 'undefined' &&
      !window.confirm(t('webAdmin.signature.cancelConfirm', { locale: LOCALE }))
    ) {
      return;
    }
    setBusy('stop');
    setError(null);
    try {
      await stopSignatureSession(session.id, t('webAdmin.signature.stopReason', { locale: LOCALE }));
      await reload();
    } catch (cause: unknown) {
      setError(errorMessage(cause) ?? t('webAdmin.signature.actionFailed', { locale: LOCALE }));
    } finally {
      setBusy('none');
    }
  }

  /**
   * „Podepsat teď se stranou" — vyda odkaz a OTEVRE podpisovou stranku strany.
   *
   * Podpis v REOS vznika JEN tam (ES1 `routeSignsInReos`: `magic-link` =
   * nahled, souhlas, SMS kod, tah prstem). Tlacitko v adminu proto podpis
   * nepredstira: udela presne to, co umi — vyda odkaz (stejne jako „Odeslat
   * další straně") a otevre ho. Bez absolutni adresy se neotevira nic, jen se
   * odkaz vypise k zkopirovani (ES2 bod 4).
   */
  async function signNow(): Promise<void> {
    if (session === null) return;
    setBusy('send');
    setError(null);
    try {
      const issued = await sendSignatureLink(session.id, 'odkaz');
      setCopied(false);
      setLink(issued === null ? null : { url: issued.link, absolute: issued.absolute });
      setNotSent(notSentMessage(issued));
      if (issued !== null && issued.absolute && typeof window !== 'undefined') {
        window.open(issued.link, '_blank', 'noopener');
      }
      await reload();
    } catch (cause: unknown) {
      setError(errorMessage(cause) ?? t('webAdmin.signature.actionFailed', { locale: LOCALE }));
    } finally {
      setBusy('none');
    }
  }

  /**
   * Strana NA RADE: prvni v poradi, ktera jeste nepodepsala a nevypadla.
   * Pocita se ze sezeni (ne ze stavu smlouvy) — poradi je cast smlouvy.
   */
  const nextSigner =
    session === null
      ? null
      : ([...session.signers]
          .sort((a, b) => a.order - b.order)
          .find(
            (s) =>
              s.status !== 'podepsano' && s.status !== 'zneplatneno' && s.status !== 'zastaveno',
          ) ?? null);

  /** Telefon strany ze slovniku smluv — sezeni cislo neposila (jen `hasPhone`). */
  function partyPhone(signer: { readonly partyKey: string; readonly partyIndex: number }): string | null {
    return (
      parties.find((p) => p.role === signer.partyKey && p.index === signer.partyIndex)?.phone ?? null
    );
  }

  return (
    <div className="rh-v4-sig" data-testid="signature-panel">
      {error !== null ? (
        <p role="alert" className="rh-a2-error" data-testid="signature-panel-error">
          {error}
        </p>
      ) : null}

      {/* 1) UROVEN: co politka rozhodla, ne co makler chce. */}
      <V4Group testId="signature-panel-policy">
        <div className="rh-v4-card">
          <div className="rh-v4-h2">{t('webAdmin.signature.policyHeading', { locale: LOCALE })}</div>
          {policy === null ? (
            <p className="rh-v4-sig__note">{t('webAdmin.signature.loading', { locale: LOCALE })}</p>
          ) : (
            <>
              <p data-testid="signature-panel-level">
                {t(keyOfLevel(policy.level), { locale: LOCALE })}
                {' · '}
                {t(keyOfRoute(policy.route), { locale: LOCALE })}
              </p>
              <p className="rh-v4-sig__note" data-testid="signature-panel-reason">
                {t(policy.reasonKey as MessageKey, { locale: LOCALE })}
              </p>
              {policy.floorApplied ? (
                <p className="rh-v4-sig__warn" data-testid="signature-panel-floor">
                  {t('webAdmin.signature.floorNote', { locale: LOCALE })}
                </p>
              ) : null}
              {policy.cadastralCapable ? (
                <p className="rh-v4-sig__note" data-testid="signature-panel-cadastre">
                  {t('webAdmin.signature.cadastralNote', { locale: LOCALE })}
                </p>
              ) : null}
            </>
          )}
        </div>
      </V4Group>

      {!loaded ? (
        /* Krok TOKYFIX4 bod 2: nez server odpovi, panel NETVRDI, ze se
           nepodepisuje — jinak nad rozepsanym podepisovanim nabizi zalozit
           druhe sezeni (viz `loaded`). */
        <V4Group testId="signature-panel-loading">
          <div className="rh-v4-card">
            <div className="rh-v4-h2">{t('webAdmin.signature.orderHeading', { locale: LOCALE })}</div>
            <p className="rh-v4-sig__note" role="status">
              {t('webAdmin.signature.loading', { locale: LOCALE })}
            </p>
          </div>
        </V4Group>
      ) : session === null ? (
        /* Jeste se nepodepisuje. Poradi stran uz ale zname ZE SMLOUVY, takze
           obrazovka odpovida na „kdo podepisuje" i tady — jen jednim seznamem
           (do kroku SML1 stala vedle panelu DRUHA tabulka stran a obe pocitaly
           stav strany po svem). */
        <V4Group testId="signature-panel-empty">
          <div className="rh-v4-card">
            <div className="rh-v4-h2">{t('webAdmin.signature.orderHeading', { locale: LOCALE })}</div>
            <p className="rh-v4-sig__note">
              {t('webAdmin.signature.noSessionNote', { locale: LOCALE })}
            </p>
          </div>
          {/* Jine `data-testid` nez u sezeni zamerne: tohle jsou STRANY
              SMLOUVY pred odeslanim, ne podepisujici se stavem podpisu. */}
          <ol className="rh-v4-steps" data-testid="signature-panel-parties">
            {parties.map((party, index) => (
              <li
                key={`${party.role}-${String(party.index)}`}
                className="rh-v4-steps__item"
                data-testid={`signature-panel-party-${String(index)}`}
              >
                <span className="rh-v4-steps__mark" aria-hidden="true">
                  {String(index + 1)}
                </span>
                <div className="rh-v4-steps__body">
                  <span className="rh-v4-steps__name">
                    {party.name ?? signerFallbackName(party.role, party.index, LOCALE)}
                  </span>
                  <span className="rh-v4-sig__note">
                    {party.name === null
                      ? t('webAdmin.signature.awaitingData', { locale: LOCALE })
                      : t('webAdmin.signature.signer.ceka', { locale: LOCALE })}
                    {party.email === null && party.phone === null
                      ? ` · ${t('webAdmin.signature.noContact', { locale: LOCALE })}`
                      : ''}
                  </span>
                </div>
              </li>
            ))}
          </ol>
          <div className="rh-v4-sect__body">
            <button
              type="button"
              className="rh-v4-btn rh-v4-btn--filled"
              onClick={() => void create()}
              // Krok PIL3 bod 2 (F03): nepripravena smlouva se k podpisu
              // neposila. Rozhoduje server; tohle je jen obrazovka, ktera to
              // rika DRIV, nez klik skonci chybou.
              disabled={busy !== 'none' || parties.length === 0 || signing?.ready === false}
              data-testid="signature-panel-create"
            >
              {t('webAdmin.signature.createButton', { locale: LOCALE })}
            </button>
            {/* Krok PIL4 bod 1: ZAKAZANE TLACITKO VZDY RIKA PROC. Smlouva bez
                jedine strany tlacitko zakazala a mlcela — makler(ka) videla
                „Založit podepisování", ktere nejde stisknout, a zadny duvod.
                Pripravenost (`signing`) ma svou vetu o radek niz. */}
            {parties.length === 0 && (
              <p className="rh-v4-sig__warn" data-testid="signature-panel-no-parties">
                {t('webAdmin.signature.noParties', { locale: LOCALE })}
              </p>
            )}
            <NotReadyNote signing={signing} fillPlan={fillPlan} onOpenChoices={onOpenChoices} />
          </div>
        </V4Group>
      ) : (
        <>
          {/* 2) PORADI PODPISU — predloha `SmlouvaPodpis.dc.html`. Kazda strana
              zvlast: stav, cas podpisu a cim byl overen. Stav smlouvy je
              ODVOZENY ze vsech (`state`) — smlouva, kde podepsal najemce a
              pronajimatel jeste neotevrel odkaz, NENI podepsana. */}
          <V4Group testId="signature-panel-order">
            <div className="rh-v4-card">
              <div className="rh-v4-h2">{t('webAdmin.signature.orderHeading', { locale: LOCALE })}</div>
              <p data-testid="signature-panel-state">
                {t('webAdmin.signature.stateHeading', { locale: LOCALE })}
                {': '}
                <b>{t(STATE_KEY[session.state], { locale: LOCALE })}</b>
              </p>
              <p className="rh-v4-sig__note" data-testid="signature-panel-version">
                {t('webAdmin.signature.versionNote', {
                  locale: LOCALE,
                  values: { version: String(session.versionNo) },
                })}
              </p>
            </div>
            <ol className="rh-v4-steps" data-testid="signature-panel-table">
              {session.signers.map((signer) => {
                const name = signer.awaitingData
                  ? signerFallbackName(signer.partyKey, signer.partyIndex, LOCALE)
                  : signer.name;
                const signed = signer.status === 'podepsano';
                const turn = signer.id === nextSigner?.id;
                return (
                  <li
                    key={signer.id}
                    className={`rh-v4-steps__item${turn ? ' rh-v4-steps__item--turn' : ''}`}
                    data-testid={`signature-panel-row-${String(signer.order)}`}
                  >
                    <span
                      className={`rh-v4-steps__mark${signed ? ' rh-v4-steps__mark--done' : turn ? ' rh-v4-steps__mark--turn' : ''}`}
                      aria-hidden="true"
                    >
                      {signed ? <Check size={16} strokeWidth={3} /> : String(signer.order + 1)}
                    </span>
                    <div className="rh-v4-steps__body">
                      <span className="rh-v4-steps__name">{name}</span>
                      <span className="rh-v4-sig__note">
                        {signer.awaitingData
                          ? `${t('webAdmin.signature.awaitingData', { locale: LOCALE })} · `
                          : ''}
                        {t(SIGNER_STATUS_KEY[signer.status], { locale: LOCALE })}
                        {signer.signedAt !== null
                          ? ` · ${t('webAdmin.signature.signedAt', {
                              locale: LOCALE,
                              values: { at: formatMoment(signer.signedAt) },
                            })}`
                          : ''}
                        {signed ? ` · ${smsProofText(signer, partyPhone(signer))}` : ''}
                      </span>
                      {/* ES2 bod 3: proc odkaz nikam neodesel. */}
                      {!signer.hasEmail && !signer.hasPhone ? (
                        <span
                          className="rh-v4-sig__note"
                          data-testid={`signature-panel-nocontact-${String(signer.order)}`}
                        >
                          {t('webAdmin.signature.noContact', { locale: LOCALE })}
                        </span>
                      ) : null}

                      {/* Strana NA RADE: shrnuti hlavnich podminek Z POLI
                          SMLOUVY, souhlas a podpis. Podpis vznika na podpisove
                          strance strany (ES1 `routeSignsInReos`), proto tlacitko
                          otevira jeji odkaz a nepredstira podpis tady. */}
                      {turn ? (
                        <div className="rh-v4-sig__turn" data-testid="signature-panel-turn">
                          <span className="rh-v4-steps__name">
                            {t('webAdmin.signature.turnTitle', {
                              locale: LOCALE,
                              values: { role: name },
                            })}
                          </span>
                          <span className="rh-v4-sig__note">
                            {t('webAdmin.signature.turnNote', { locale: LOCALE })}
                          </span>
                          <div className="rh-v4-sig__cond" data-testid="signature-panel-conditions">
                            {conditions.map((item) => (
                              <span
                                key={item.key}
                                className="rh-v4-sig__cond-item"
                                data-testid={`signature-panel-condition-${item.key}`}
                              >
                                <span className="rh-v4-sig__note">
                                  {t(item.labelKey, { locale: LOCALE })}
                                </span>
                                <b>{conditionValue(item)}</b>
                              </span>
                            ))}
                          </div>
                          <label className="rh-v4-check">
                            <input
                              type="checkbox"
                              checked={consent}
                              onChange={(e) => setConsent(e.target.checked)}
                              data-testid="signature-panel-consent"
                            />
                            <span>{t('webAdmin.signature.consent', { locale: LOCALE })}</span>
                          </label>
                          <button
                            type="button"
                            className="rh-v4-btn rh-v4-btn--filled"
                            onClick={() => void signNow()}
                            disabled={busy !== 'none' || !consent}
                            data-testid="signature-panel-sign-now"
                          >
                            {t('webAdmin.signature.signNow', { locale: LOCALE })}
                          </button>
                          <span className="rh-v4-sig__note">
                            {t('webAdmin.signature.signNowNote', { locale: LOCALE })}
                          </span>
                        </div>
                      ) : null}
                    </div>
                  </li>
                );
              })}
            </ol>
            <div className="rh-v4-sect__body">
              <span className="rh-v4-steps__name">
                {t('webAdmin.signature.afterHeading', { locale: LOCALE })}
              </span>
              <p className="rh-v4-sig__note">{t('webAdmin.signature.afterNote', { locale: LOCALE })}</p>
            </div>
          </V4Group>

          {/* 3) DUKAZ PODPISU — otisk z pecete (ES1), „text beze změny od
              odeslání" z kontroly pecete a uroven. Interni pecet NENI
              kvalifikovane casove razitko a panel to pise. */}
          <V4Group testId="signature-panel-proof">
            <div className="rh-v4-card">
              <div className="rh-v4-h2">{t('webAdmin.signature.proofHeading', { locale: LOCALE })}</div>
              {evidence !== null ? (
                <p
                  className={
                    evidence.sealVerification?.valid === false ? 'rh-v4-sig__warn' : 'rh-v4-sig__ok'
                  }
                  data-testid="signature-panel-unchanged"
                >
                  {evidence.sealVerification?.valid === false
                    ? t('webAdmin.signature.proofChanged', { locale: LOCALE })
                    : t('webAdmin.signature.proofUnchanged', { locale: LOCALE })}
                </p>
              ) : null}
              {policy !== null ? (
                <p className="rh-v4-sig__note" data-testid="signature-panel-proof-level">
                  {t('webAdmin.signature.proofLevel', {
                    locale: LOCALE,
                    values: { level: t(keyOfLevel(policy.level), { locale: LOCALE }) },
                  })}
                </p>
              ) : null}
              {session.seal === null ? (
                <p className="rh-v4-sig__note" data-testid="signature-panel-noseal">
                  {t('webAdmin.signature.noSeal', { locale: LOCALE })}
                </p>
              ) : (
                <div className="rh-v4-sig__seal" data-testid="signature-panel-seal">
                  <span className="rh-v4-sig__note">
                    {t('webAdmin.signature.proofSeal', {
                      locale: LOCALE,
                      values: { at: formatMoment(session.seal.sealedAt) },
                    })}
                  </span>
                  <span className="rh-v4-sig__hash">{session.seal.sealHash}</span>
                  <span className="rh-v4-sig__note">
                    {t('webAdmin.signature.sealHeading', { locale: LOCALE })}
                    {': '}
                    {t(session.seal.labelKey as MessageKey, { locale: LOCALE })}
                  </span>
                </div>
              )}
            </div>

            {/* 4) DŮKAZNÍ BALÍČEK — co chybí, ne jen „hotovo“. */}
            {evidence === null ? null : (
              <div className="rh-v4-sect__body" data-testid="signature-panel-evidence">
                <span className="rh-v4-steps__name">
                  {t('webAdmin.signature.evidenceHeading', { locale: LOCALE })}
                </span>
                {evidence.sealVerification?.valid === false ? (
                  <p className="rh-v4-sig__warn" data-testid="signature-panel-evidence-invalid">
                    {t('webAdmin.signature.evidenceInvalid', { locale: LOCALE })}
                  </p>
                ) : null}
                <ul className="rh-v4-sig__list" data-testid="signature-panel-evidence-items">
                  {evidence.completeness.items.map((item) => (
                    <li key={item.key} data-ok={item.ok ? 'yes' : 'no'}>
                      {t(item.labelKey as MessageKey, { locale: LOCALE })}
                      {item.ok ? '' : ` — ${t('webAdmin.signature.missing', { locale: LOCALE })}`}
                    </li>
                  ))}
                </ul>
                <p data-testid="signature-panel-exportable">
                  {evidence.exportable
                    ? t('webAdmin.signature.exportable', { locale: LOCALE })
                    : t('webAdmin.signature.notExportable', { locale: LOCALE })}
                </p>
                {evidence.exportable ? (
                  <button
                    type="button"
                    className="rh-v4-btn rh-v4-btn--tinted"
                    onClick={() => downloadEvidence(evidence)}
                    data-testid="signature-panel-evidence-download"
                  >
                    {t('webAdmin.signature.downloadEvidence', { locale: LOCALE })}
                  </button>
                ) : (
                  <p className="rh-v4-sig__note" data-testid="signature-panel-evidence-blocked">
                    {t('webAdmin.signature.downloadEvidenceBlocked', { locale: LOCALE })}
                  </p>
                )}
              </div>
            )}
          </V4Group>

          {/* 5) TISKOVA CESTA — kontrolni seznam; REOS na katastr nic neposila. */}
          {print === null ? null : (
            <V4Group testId="signature-panel-print">
              <div className="rh-v4-card">
                <div className="rh-v4-h2">
                  {t('webAdmin.signature.printHeading', { locale: LOCALE })}
                </div>
                <ul className="rh-v4-sig__list">
                  {print.checklist.map((item) => (
                    <li key={item.kind} data-testid={`signature-panel-print-${item.kind}`}>
                      {t(item.labelKey as MessageKey, { locale: LOCALE })}
                    </li>
                  ))}
                </ul>
                {print.submitsToCadastre ? null : (
                  <p className="rh-v4-sig__note" data-testid="signature-panel-no-cadastre">
                    {t('webAdmin.signature.noCadastreSubmit', { locale: LOCALE })}
                  </p>
                )}
              </div>
            </V4Group>
          )}

          {/* ES2 body 3 + 4: odkaz se vraci JEDINKRAT, takze ho panel nesmi
              jen vypsat a zapomenout — je u nej tlacitko „Kopírovat odkaz“.
              A kdyz odkaz NENI absolutni (server nema nastavenou adresu
              verejneho webu), panel to rekne: `/podpis/KT5P…` nikde neotevre. */}
          {link !== null ? (
            <V4Group testId="signature-panel-link-box">
              <div className="rh-v4-card">
                {/* MAIL1 bod 3: nejdřív PRAVDA o odeslání, až pak odkaz. Tiché
                    „odesláno" by makléře nechalo čekat na klienta, který nic
                    nedostal. */}
                {notSent !== null ? (
                  <p className="rh-v4-sig__warn" role="alert" data-testid="signature-panel-not-sent">
                    {notSent}
                  </p>
                ) : null}
                <p className="rh-v4-sig__hash" data-testid="signature-panel-link">
                  {t('webAdmin.signature.linkOnce', { locale: LOCALE })}
                  {': '}
                  {link.url}
                </p>
                <button
                  type="button"
                  className="rh-v4-btn rh-v4-btn--tinted"
                  onClick={() => void copyLink(link.url)}
                  data-testid="signature-panel-copy"
                >
                  {t('webAdmin.signature.copyLink', { locale: LOCALE })}
                </button>
                {copied ? (
                  <span data-testid="signature-panel-copied">
                    {t('webAdmin.signature.copied', { locale: LOCALE })}
                  </span>
                ) : null}
                {!link.absolute ? (
                  <p className="rh-v4-sig__warn" data-testid="signature-panel-link-relative">
                    {t('webAdmin.signature.linkRelative', { locale: LOCALE })}
                  </p>
                ) : null}
              </div>
            </V4Group>
          ) : null}

          <div className="rh-v4-sig__acts">
            <button
              type="button"
              className="rh-v4-btn rh-v4-btn--tinted"
              onClick={() => void send()}
              disabled={busy !== 'none' || session.status === 'podepsano'}
              data-testid="signature-panel-send"
            >
              {t('webAdmin.signature.sendNext', { locale: LOCALE })}
            </button>
            <button
              type="button"
              className="rh-v4-btn rh-v4-btn--tinted"
              onClick={() => void remind()}
              disabled={busy !== 'none' || session.status !== 'rozeslano'}
              data-testid="signature-panel-remind"
            >
              {t('webAdmin.signature.remindButton', { locale: LOCALE })}
            </button>
          </div>

          {/*
            6) NOVE KOLO PODEPISOVANI (krok PRF4, vada V-NEW3).
            Zivy pruchod 13. 10. 2026: po „Zastavit podepisování" (a po
            zneplatneni podpisu zmenou textu) panel dalsi kolo nenabidl —
            makler(ka) se k nemu dostala jen oklikou pres zalozku „Odeslat
            stranám". Tlacitko je TATAZ CESTA jako prvni zalozeni (`create`,
            tentyz `data-testid`), vcetne brany pripravenosti; obe vetve panelu
            jsou vylucne, takze dve tlacitka nikdy nestoji vedle sebe.
          */}
          {(session.status === 'zastaveno' || session.status === 'neplatne') && (
            <V4Group testId="signature-panel-restart">
              <div className="rh-v4-card">
                <div className="rh-v4-h2">
                  {t('webAdmin.signature.restartHeading', { locale: LOCALE })}
                </div>
                <button
                  type="button"
                  className="rh-v4-btn rh-v4-btn--filled"
                  onClick={() => void create()}
                  disabled={busy !== 'none' || parties.length === 0 || signing?.ready === false}
                  data-testid="signature-panel-create"
                >
                  {t('webAdmin.signature.restartButton', { locale: LOCALE })}
                </button>
                <p className="rh-v4-sig__note" data-testid="signature-panel-restart-note">
                  {t('webAdmin.signature.restartNote', { locale: LOCALE })}
                </p>
                {parties.length === 0 && (
                  <p className="rh-v4-sig__warn" data-testid="signature-panel-no-parties">
                    {t('webAdmin.signature.noParties', { locale: LOCALE })}
                  </p>
                )}
                <NotReadyNote signing={signing} fillPlan={fillPlan} onOpenChoices={onOpenChoices} />
              </div>
            </V4Group>
          )}

          {/* 7) ZRUSIT PODEPISOVANI — doména to umí (`stopSignatureSession`):
              podpisy zustanou v auditni stope, ale NEPLATI. Podepsanou smlouvu
              server zastavit nenecha (409), proto to tlacitko rika predem. */}
          <V4Group testId="signature-panel-cancel">
            <div className="rh-v4-card">
              <div className="rh-v4-h2">{t('webAdmin.signature.cancelHeading', { locale: LOCALE })}</div>
              <button
                type="button"
                className="rh-v4-btn rh-v4-btn--danger"
                onClick={() => void stop()}
                disabled={busy !== 'none' || session.status === 'podepsano'}
                data-testid="signature-panel-stop"
              >
                {t('webAdmin.signature.stopButton', { locale: LOCALE })}
              </button>
              <p className="rh-v4-sig__note" data-testid="signature-panel-cancel-note">
                {session.status === 'podepsano'
                  ? t('webAdmin.signature.cancelSigned', { locale: LOCALE })
                  : t('webAdmin.signature.cancelNote', { locale: LOCALE })}
              </p>
            </div>
          </V4Group>
        </>
      )}

      {/* 8) HISTORIE Z UDALOSTI (`/timeline`) — lidska veta, ne strojovy enum. */}
      <V4Group testId="signature-panel-history">
        <div className="rh-v4-card">
          <div className="rh-v4-h2">{t('webAdmin.signature.historyHeading', { locale: LOCALE })}</div>
          {timeline.length === 0 ? (
            <p className="rh-v4-sig__note">{t('webAdmin.signature.historyEmpty', { locale: LOCALE })}</p>
          ) : (
            <ol className="rh-v4-sig__list" data-testid="signature-panel-timeline">
              {[...timeline].reverse().map((event, i) => (
                <li key={`${event.kind}-${String(i)}`}>
                  <span className="rh-v4-sig__when">{formatMoment(event.createdAt)}</span>{' '}
                  {timelineEventLabel(event.kind)}
                </li>
              ))}
            </ol>
          )}
        </div>
      </V4Group>
    </div>
  );
}

/**
 * Posledni sezeni u smlouvy. List je vraceny sestupne, takze prvni radek je
 * nejnovějsi proces podepisovani; historie zustava v DB (a v casove ose).
 */
async function getSession(contractId: string): Promise<SignatureSessionDto | null> {
  const rows = await listSignatureSessions({ contractId });
  return rows[0] ?? null;
}
